import IHttpClient from '@sunbit-dev/common/services/httpClient/IHttpClient';
import { ISendMember, Environments, IEnvironmentsStatusResponse, IEnvironmentStatus, ITrack } from '../constants/models';
import mixpanel from 'mixpanel-browser';
import { datadogRum } from '@datadog/browser-rum';

enum Methods {
  post = 'post',
  put = 'put',
  get = 'get',
  del = 'del',
  patch = 'patch',
}

class SunbitApi {
  private httpClient!: IHttpClient;

  public init(httpClient: IHttpClient) {
    this.httpClient = httpClient;
    return this;
  }

  public setToken(userToken: string) {
    this.httpClient.setHeader('Authorization', `Bearer ${userToken}`);
  }

  public removeToken() {
    this.httpClient.setHeader('Authorization', '');
  }

  private async request({ method, url, headers, data, trackInfo }: { method?: Methods; headers?: object; url: string; data?: any; trackInfo?: ITrack }) {
    // Set the headers
    if (headers) Object.keys(headers).map((key) => this.httpClient.setHeader(key, headers[key]));
    try {
      const response = await this.httpClient[method || Methods.get]?.(url, data);

      if (trackInfo) mixpanel.track(trackInfo.eventName, trackInfo.props);

      return response.data;
    } catch (e) {
      const error = e as Error;
      if (trackInfo) {
        if (error?.message?.includes('403')) {
          const errorName = 'Error - unauthorized';
          datadogRum.addUserAction(errorName, trackInfo.props);
          mixpanel.track(errorName, trackInfo.props);
        } else {
          const errorName = 'Error - ' + trackInfo.eventName;
          datadogRum.addUserAction(errorName, trackInfo.props);
          mixpanel.track(errorName, trackInfo.props);
        }
      }
      throw error;
    }
  }

  // Auth Controller
  public async login(username: string, password: string, trackInfo?: ITrack) {
    return this.request({ method: Methods.post, url: 'api/v1/auth/login', data: { username, password }, trackInfo });
  }

  public getAllianceData(trackInfo?: ITrack) {
    return this.request({ url: 'api/v1/auth/me', trackInfo });
  }

  public resetPassword(email: string) {
    return this.request({ method: Methods.post, url: 'api/v1/auth/forgot-password', data: { email } });
  }

  public register(username: string, password: string, registrationToken: string) {
    return this.request({ method: Methods.post, url: 'api/v1/auth/register', data: { username, password, registrationToken } });
  }

  // Keys Controller
  public getUserKeys(trackInfo?: ITrack) {
    return this.request({ url: 'api/v1/keys', trackInfo });
  }

  // Members Controller
  public addMembers(newMember: ISendMember, trackInfo?: ITrack) {
    return this.request({ method: Methods.post, url: 'api/v1/members', data: { ...newMember, role: 'OWNER' }, trackInfo });
  }

  public getUserOrgMembers(trackInfo?: ITrack) {
    return this.request({ url: 'api/v1/members', trackInfo });
  }

  // WebHooks Controller
  public updateWebhookUrl(url: string, environment: Environments, trackInfo?: ITrack) {
    return this.request({ method: Methods.post, url: 'api/v1/webhooks', trackInfo, data: { url, environment } });
  }

  public requestProdAccess = async (trackInfo?: ITrack): Promise<IEnvironmentStatus> => {
    return this.request({ method: Methods.post, url: 'api/v1/environments', data: { name: 'PRODUCTION' }, trackInfo });
  };

  public getRequestProdStatus = async (trackInfo?: ITrack): Promise<IEnvironmentsStatusResponse> => {
    return this.request({ url: 'api/v1/environments', trackInfo });
  };

  public testWebhookUrl(url: string, key: string, trackInfo?: ITrack) {
    return this.request({ method: Methods.post, url: 'api/v1/webhooks/test', data: { url, key }, trackInfo });
  }

  public getUserOrgWebhookData(trackInfo?: ITrack) {
    return this.request({ url: 'api/v1/webhooks', trackInfo });
  }

  // Settings Controller
  public deleteAllData(trackInfo?: ITrack) {
    return this.request({ method: Methods.del, url: 'api/v1/environments', trackInfo });
  }
}

export default new SunbitApi();
